<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
      <div class="d-flex align-items-center">
        <am-button
          variant="outline-primary"
          icon="ArrowLeftIcon"
          label="Back"
          class="mr-1"
          @click="$router.push({ name: 'employees' })"
        />
        <h3 class="mb-0 ml-50">
          {{ employee ? employee.full_name : 'Employee details' }}
        </h3>
      </div>
      <am-button
        v-if="employee"
        icon="Edit2Icon"
        label="Edit"
        @click="setupOpened = true"
      />
    </div>

    <div
      v-if="loading"
      class="employee-detail-loading text-center py-5"
    >
      <b-spinner variant="primary" />
      <div class="text-muted mt-1">
        Loading employee details...
      </div>
    </div>

    <template v-else-if="employee">
      <b-row>
        <b-col md="6">
          <b-card class="mb-2">
            <h5>Profile</h5>
            <p><strong>ID:</strong> {{ employee.employee_code }}</p>
            <p><strong>Type:</strong> {{ employee.employee_type === 'staff' ? 'Staff' : 'Union Executive' }}</p>
            <p><strong>Gender:</strong> {{ employee.gender || '-' }}</p>
            <p><strong>Position:</strong> {{ employee.position }}</p>
            <p><strong>Mobile:</strong> {{ employee.mobile }}</p>
            <p><strong>Email:</strong> {{ employee.email || '-' }}</p>
            <p class="mb-0"><strong>Address:</strong> {{ employee.address }}</p>
          </b-card>
        </b-col>
        <b-col md="6">
          <b-card
            v-if="employee.employee_type === 'staff'"
            class="mb-2"
          >
            <h5>Employment</h5>
            <p><strong>Department:</strong> {{ employee.department ? employee.department.name : '-' }}</p>
            <p><strong>Date employed:</strong> {{ formatDate(employee.employment_date) }}</p>
            <p :class="{ 'mb-0': !(employee.status === 'Inactive' && employee.inactive_reason) }">
              <strong>Status:</strong>
              <b-badge
                class="ml-50 align-middle"
                :variant="statusBadgeVariant(employee.status)"
              >
                {{ employee.status || '—' }}
              </b-badge>
            </p>
            <p
              v-if="employee.status === 'Inactive' && employee.inactive_reason"
              class="mb-0"
            >
              <strong>Inactive Reason:</strong> {{ employee.inactive_reason }}
            </p>
          </b-card>
          <b-card
            v-else
            class="mb-2"
          >
            <h5>Leadership term</h5>
            <p><strong>Committee:</strong> {{ employee.committee || '-' }}</p>
            <p><strong>Elected / appointed:</strong> {{ formatDate(employee.date_elected_appointed) }}</p>
            <p><strong>Method:</strong> {{ employee.appointment_method || '-' }}</p>
            <p><strong>Term:</strong> {{ formatDate(employee.term_start) }} → {{ formatDate(employee.term_end) }}</p>
            <p><strong>Term status:</strong>
              <b-badge
                class="ml-50 align-middle"
                :variant="statusBadgeVariant(employee.term_status)"
              >
                {{ employee.term_status || '—' }}
              </b-badge>
            </p>
            <p><strong>Eligible for re-election:</strong> {{ employee.eligible_reelection || '-' }}</p>
            <p class="mb-0"><strong>Previous position:</strong> {{ employee.previous_position || '-' }}</p>
          </b-card>

          <b-card class="mb-2">
            <h5>Emergency contact</h5>
            <p><strong>Name:</strong> {{ employee.emergency_name }}</p>
            <p><strong>Relationship:</strong> {{ employee.emergency_relation }}</p>
            <p class="mb-0"><strong>Phone:</strong> {{ employee.emergency_phone }}</p>
          </b-card>
        </b-col>
      </b-row>

      <b-card v-if="employee.notes">
        <h5>Notes</h5>
        <p class="mb-0">{{ employee.notes }}</p>
      </b-card>
    </template>

    <employee-setup
      :modal-opened="setupOpened"
      :employee="employee"
      @modalClosed="setupOpened = false"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BSpinner, BBadge,
} from 'bootstrap-vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import EmployeeSetup from '@/views/employee-centre/employees/EmployeeSetup.vue'
import showToast from '@/farmbusiness/mixins/showToast'

export default {
  name: 'EmployeeDetail',
  components: {
    AmButton, EmployeeSetup, BRow, BCol, BCard, BSpinner, BBadge,
  },
  mixins: [showToast],
  data() {
    return {
      loading: false,
      setupOpened: false,
    }
  },
  computed: {
    employee() {
      return this.$store.getters['employeeCentre/currentEmployee']
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      this.$store.dispatch('employeeCentre/fetchEmployee', this.$route.params.id)
        .finally(() => { this.loading = false })
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },
    statusBadgeVariant(status) {
      if (status === 'Inactive') return 'light-danger'
      if (status === 'Active' || status === 'Serving') return 'light-success'
      if (status === 'On leave') return 'light-warning'
      return 'light-secondary'
    },
    onSaved() {
      this.setupOpened = false
      this.load()
    },
  },
}
</script>

<style scoped>
.employee-detail-loading {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
